/**
 * Shared form fields for the staff <-> schedule mapping (create/edit).
 *
 * Maps a staff member (role 3) to a schedule (the `departments` resource,
 * sent as `group_id`) within an organization. A single staff member can be
 * mapped to several schedules: each mapping is a separate row.
 *
 * The backend stores the relation in `user_group_organizations`
 * ({ organization_id, group_id, staff_id, is_primary }). On edit the three
 * identifying fields are locked: changing the target of an existing mapping
 * is done by deleting it and creating a new one.
 */

import {
  AutocompleteInput,
  BooleanInput,
  SelectInput,
  required,
  useGetList,
  useTranslate,
} from 'react-admin';
import { useWatch } from 'react-hook-form';

const ScheduleAndStaffInputs = ({ isCreate }: { isCreate: boolean }) => {
  const translate = useTranslate();
  const organizationId = useWatch({ name: 'organization_id' });

  const { data: schedules } = useGetList('departments', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'name', order: 'ASC' },
  });
  const { data: staff } = useGetList('staff', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'full_name', order: 'ASC' },
  });

  const scheduleChoices = (schedules ?? [])
    .filter((schedule: any) => !organizationId || schedule.organization_id === organizationId)
    .map((schedule: any) => ({ id: schedule.department_id, name: schedule.name }));

  const staffChoices = (staff ?? [])
    .filter((member: any) => !organizationId || member.organization_id === organizationId)
    .map((member: any) => ({ id: member.user_id, name: member.full_name }));

  return (
    <>
      {/* group_id is the schedule (department) id the backend expects */}
      <SelectInput
        source="group_id"
        validate={required()}
        disabled={!isCreate}
        choices={scheduleChoices}
        label={translate('resources.userGroupOrganization.fields.group_id')}
      />
      <AutocompleteInput
        source="staff_id"
        validate={required()}
        disabled={!isCreate}
        choices={staffChoices}
        label={translate('resources.userGroupOrganization.fields.staff_id')}
      />
    </>
  );
};

export const StaffScheduleFormFields = ({ isCreate }: { isCreate: boolean }) => {
  const translate = useTranslate();
  const { data: organizations } = useGetList('organizations', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'full_name', order: 'ASC' },
  });

  return (
    <>
      <SelectInput
        source="organization_id"
        validate={required()}
        disabled={!isCreate}
        choices={(organizations ?? []).map((org: any) => ({
          id: org.user_id,
          name: org.company_name || org.full_name,
        }))}
        label={translate('resources.userGroupOrganization.fields.organization_id')}
      />
      <ScheduleAndStaffInputs isCreate={isCreate} />
      <BooleanInput
        source="is_primary"
        format={(value: any) => Boolean(value)}
        parse={(value: any) => (value ? 1 : 0)}
        label={translate('resources.userGroupOrganization.fields.is_primary')}
      />
    </>
  );
};

export default StaffScheduleFormFields;
